/**
 * Terms and Conditions Page
 *
 * Legal terms and conditions for using PetMas services.
 */

import { Link } from 'react-router-dom';

const LAST_UPDATED = '15 de Enero de 2024';

export function TermsPage() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-primary to-primary-dark text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <DocumentIcon />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Terminos y Condiciones
          </h1>
          <p className="text-lg opacity-90">
            Ultima actualizacion: {LAST_UPDATED}
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Table of Contents */}
          <nav className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-12">
            <h2 className="font-semibold text-gray-900 mb-4">Contenido</h2>
            <ol className="space-y-2 text-sm">
              <li>
                <a href="#general" className="text-primary hover:underline">1. Informacion General</a>
              </li>
              <li>
                <a href="#uso" className="text-primary hover:underline">2. Uso del Sitio Web</a>
              </li>
              <li>
                <a href="#cuenta" className="text-primary hover:underline">3. Cuenta de Usuario</a>
              </li>
              <li>
                <a href="#productos" className="text-primary hover:underline">4. Productos y Precios</a>
              </li>
              <li>
                <a href="#compras" className="text-primary hover:underline">5. Proceso de Compra</a>
              </li>
              <li>
                <a href="#envios" className="text-primary hover:underline">6. Envios y Entregas</a>
              </li>
              <li>
                <a href="#devoluciones" className="text-primary hover:underline">7. Devoluciones y Reembolsos</a>
              </li>
              <li>
                <a href="#propiedad" className="text-primary hover:underline">8. Propiedad Intelectual</a>
              </li>
              <li>
                <a href="#limitacion" className="text-primary hover:underline">9. Limitacion de Responsabilidad</a>
              </li>
              <li>
                <a href="#modificaciones" className="text-primary hover:underline">10. Modificaciones</a>
              </li>
              <li>
                <a href="#contacto" className="text-primary hover:underline">11. Contacto</a>
              </li>
            </ol>
          </nav>

          {/* Content Sections */}
          <div className="prose prose-gray max-w-none">
            <section id="general" className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                1. Informacion General
              </h2>
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <p className="text-gray-600 mb-4">
                  Bienvenido a PetMas (www.petmas.pe), una tienda virtual especializada en
                  productos para mascotas operada por PETMAS S.A.C., con RUC 20XXXXXXXXX,
                  domiciliada en Lima, Peru.
                </p>
                <p className="text-gray-600 mb-4">
                  Al acceder y utilizar nuestro sitio web, usted acepta cumplir con estos
                  Terminos y Condiciones. Si no esta de acuerdo con alguna parte de estos
                  terminos, le solicitamos que no utilice nuestros servicios.
                </p>
                <p className="text-gray-600">
                  Estos terminos aplican a todos los usuarios del sitio, incluyendo visitantes,
                  clientes registrados y cualquier persona que acceda a nuestros servicios.
                </p>
              </div>
            </section>

            <section id="uso" className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                2. Uso del Sitio Web
              </h2>
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <p className="text-gray-600 mb-4">
                  Al utilizar nuestro sitio web, usted se compromete a:
                </p>
                <ul className="list-disc list-inside text-gray-600 space-y-2 mb-4">
                  <li>Proporcionar informacion veraz, exacta y actualizada</li>
                  <li>Utilizar el sitio unicamente para fines legales</li>
                  <li>No interferir con el funcionamiento normal del sitio</li>
                  <li>No intentar acceder a areas restringidas del sistema</li>
                  <li>Respetar los derechos de propiedad intelectual</li>
                  <li>No utilizar el sitio para actividades fraudulentas</li>
                </ul>
                <p className="text-gray-600">
                  Nos reservamos el derecho de suspender o cancelar el acceso a cualquier
                  usuario que viole estos terminos sin previo aviso.
                </p>
              </div>
            </section>

            <section id="cuenta" className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                3. Cuenta de Usuario
              </h2>
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <p className="text-gray-600 mb-4">
                  Para realizar compras, puede crear una cuenta proporcionando la informacion
                  requerida. Usted es responsable de:
                </p>
                <ul className="list-disc list-inside text-gray-600 space-y-2 mb-4">
                  <li>Mantener la confidencialidad de sus credenciales de acceso</li>
                  <li>Notificarnos inmediatamente de cualquier uso no autorizado</li>
                  <li>Mantener su informacion de contacto actualizada</li>
                  <li>Todas las actividades realizadas bajo su cuenta</li>
                </ul>
                <p className="text-gray-600">
                  Nos reservamos el derecho de rechazar o cancelar cuentas a nuestra discrecion,
                  especialmente en casos de sospecha de fraude o violacion de estos terminos.
                </p>
              </div>
            </section>

            <section id="productos" className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                4. Productos y Precios
              </h2>
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <h3 className="font-semibold text-gray-900 mb-3">4.1 Descripcion de Productos</h3>
                <p className="text-gray-600 mb-4">
                  Nos esforzamos por describir nuestros productos con precision. Sin embargo,
                  no garantizamos que las descripciones, imagenes o contenido sean completamente
                  exactos, completos o libres de errores. Los colores pueden variar dependiendo
                  de la configuracion de su pantalla.
                </p>

                <h3 className="font-semibold text-gray-900 mb-3">4.2 Precios</h3>
                <p className="text-gray-600 mb-4">
                  Todos los precios estan expresados en Soles Peruanos (PEN) e incluyen IGV.
                  Nos reservamos el derecho de modificar precios sin previo aviso. El precio
                  aplicable sera el vigente al momento de confirmar su pedido.
                </p>

                <h3 className="font-semibold text-gray-900 mb-3">4.3 Disponibilidad</h3>
                <p className="text-gray-600">
                  La disponibilidad de productos esta sujeta a existencias. En caso de que
                  un producto no este disponible despues de su compra, le notificaremos y
                  ofreceremos alternativas o reembolso completo.
                </p>
              </div>
            </section>

            <section id="compras" className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                5. Proceso de Compra
              </h2>
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <h3 className="font-semibold text-gray-900 mb-3">5.1 Pedidos</h3>
                <p className="text-gray-600 mb-4">
                  Al realizar un pedido, usted hace una oferta de compra. El contrato de
                  venta se perfecciona cuando enviamos la confirmacion del pedido a su
                  correo electronico.
                </p>

                <h3 className="font-semibold text-gray-900 mb-3">5.2 Metodos de Pago</h3>
                <p className="text-gray-600 mb-4">
                  Aceptamos tarjetas de credito y debito (Visa, Mastercard, American Express),
                  transferencias bancarias, Yape, Plin y pago contra entrega en Lima. Todos
                  los pagos son procesados de forma segura.
                </p>

                <h3 className="font-semibold text-gray-900 mb-3">5.3 Comprobantes</h3>
                <p className="text-gray-600">
                  Emitimos boletas o facturas electronicas segun su solicitud. Para factura,
                  debera proporcionar su RUC y razon social al momento de la compra.
                </p>
              </div>
            </section>

            <section id="envios" className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                6. Envios y Entregas
              </h2>
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <p className="text-gray-600 mb-4">
                  Realizamos envios a nivel nacional. Los tiempos y costos de envio varian
                  segun la ubicacion y peso del pedido:
                </p>
                <ul className="list-disc list-inside text-gray-600 space-y-2 mb-4">
                  <li><strong>Lima Metropolitana:</strong> 24-48 horas habiles</li>
                  <li><strong>Provincias:</strong> 3-7 dias habiles segun destino</li>
                  <li><strong>Envio gratis:</strong> Pedidos mayores a S/100 en Lima</li>
                </ul>
                <p className="text-gray-600">
                  El comprador es responsable de proporcionar una direccion de entrega
                  correcta y de asegurar que haya alguien para recibir el pedido.
                </p>
              </div>
            </section>

            <section id="devoluciones" className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                7. Devoluciones y Reembolsos
              </h2>
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <p className="text-gray-600 mb-4">
                  Aceptamos devoluciones dentro de los 7 dias calendario posteriores a la
                  recepcion del producto, sujeto a las siguientes condiciones:
                </p>
                <ul className="list-disc list-inside text-gray-600 space-y-2 mb-4">
                  <li>El producto debe estar sin usar y en su empaque original</li>
                  <li>No se aceptan devoluciones de alimentos abiertos</li>
                  <li>Productos personalizados no son elegibles para devolucion</li>
                  <li>Productos danados o defectuosos se cambian sin costo</li>
                </ul>
                <p className="text-gray-600">
                  Para mas detalles, consulte nuestra{' '}
                  <Link to="/devoluciones" className="text-primary hover:underline">
                    Politica de Devoluciones
                  </Link>.
                </p>
              </div>
            </section>

            <section id="propiedad" className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                8. Propiedad Intelectual
              </h2>
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <p className="text-gray-600 mb-4">
                  Todo el contenido del sitio web, incluyendo pero no limitado a textos,
                  graficos, logos, iconos, imagenes, clips de audio, descargas digitales
                  y compilaciones de datos, es propiedad de PetMas o de sus proveedores
                  de contenido y esta protegido por las leyes de propiedad intelectual.
                </p>
                <p className="text-gray-600">
                  Esta prohibido reproducir, distribuir, modificar, transmitir, exhibir
                  o utilizar cualquier contenido sin autorizacion previa por escrito.
                </p>
              </div>
            </section>

            <section id="limitacion" className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                9. Limitacion de Responsabilidad
              </h2>
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <p className="text-gray-600 mb-4">
                  PetMas no sera responsable por danos indirectos, incidentales, especiales,
                  consecuentes o punitivos, incluyendo perdida de beneficios, datos o uso,
                  derivados del uso o imposibilidad de uso del sitio o productos.
                </p>
                <p className="text-gray-600 mb-4">
                  Nuestra responsabilidad maxima no excedera el monto pagado por usted
                  en la transaccion especifica que origino el reclamo.
                </p>
                <p className="text-gray-600">
                  Las recomendaciones de productos y consejos proporcionados en el sitio
                  o por nuestro chatbot de IA son orientativos y no sustituyen la consulta
                  veterinaria profesional.
                </p>
              </div>
            </section>

            <section id="modificaciones" className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                10. Modificaciones
              </h2>
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <p className="text-gray-600">
                  Nos reservamos el derecho de modificar estos Terminos y Condiciones en
                  cualquier momento. Los cambios entraran en vigor inmediatamente despues
                  de su publicacion en el sitio web. El uso continuado del sitio despues
                  de cualquier modificacion constituye la aceptacion de los nuevos terminos.
                </p>
              </div>
            </section>

            <section id="contacto" className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                11. Contacto
              </h2>
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <p className="text-gray-600 mb-4">
                  Para cualquier consulta sobre estos Terminos y Condiciones, puede
                  contactarnos a traves de:
                </p>
                <ul className="text-gray-600 space-y-2">
                  <li><strong>Email:</strong> legal@petmas.pe</li>
                  <li><strong>Telefono:</strong> (01) 444-5555</li>
                  <li><strong>Direccion:</strong> Lima, Peru</li>
                </ul>
              </div>
            </section>
          </div>

          {/* CTA */}
          <div className="bg-gray-50 rounded-2xl p-8 text-center">
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              ¿Tienes alguna pregunta?
            </h3>
            <p className="text-gray-600 mb-6">
              Nuestro equipo legal esta disponible para aclarar cualquier duda.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/ayuda"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary text-white font-medium rounded-lg hover:bg-primary-dark transition-colors"
              >
                Centro de Ayuda
              </Link>
              <Link
                to="/privacidad"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white text-gray-700 font-medium rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
              >
                Politica de Privacidad
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function DocumentIcon() {
  return (
    <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  );
}
